import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import WandSparkles from 'lucide-react/dist/esm/icons/wand-sparkles';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import { injectIntl } from 'react-intl';
import { SlideTransition } from '../../Transition/Transition';
import { toggleDialog } from '../../../store/control/duck';
import { getClientById } from '../../../store/client/duck';
import { updateFactsheetById } from '../../../store/client/api';
import { trackDirect } from '../../../store/analytics/api';
import IconToast from '../../NewToast';
import { toast } from 'react-toastify';
import {
  buildMeetingFactsheetSyncPayload,
  isMeetingFactsheetSyncConflict
} from '../../../utils/meeting-note-factsheet-sync';
import './MeetingFactsheetSyncDialog.scss';

const isBlank = value => value === undefined || value === null || value === '';
const isDigit = char => /[0-9]/.test(char || '');

const displayValue = (value, metadata, intl) => {
  if (isBlank(value)) return intl.formatMessage({ id: 'N/A' });
  if (metadata.inputType === 'number' && Number.isFinite(Number(value))) return intl.formatNumber(Number(value));
  if (metadata.inputType === 'date') return intl.formatDate(value, { dateStyle: 'medium', timeZone: 'UTC' });
  if (metadata.inputType === 'enum') return intl.formatMessage({ id: value, defaultMessage: value });
  return String(value);
};
const displayConfidence = (confidence, intl) =>
  intl.formatMessage({ id: `meeting-factsheet-confidence-${confidence}`, defaultMessage: confidence });
const EMPTY_SUGGESTIONS = [];

const displayLabel = (item, intl) =>
  intl.formatMessage({ id: item.metadata.labelId, defaultMessage: item.metadata.label });

// ponytail: plain substring match; a number only counts when it is not part of a longer number
// ("1" must not light up inside "2021"). Values the AI paraphrased ("2 million") just go unmarked.
export const findValueInEvidence = (evidence, value, inputType) => {
  if (!evidence || isBlank(value)) return -1;
  const haystack = String(evidence).toLowerCase();
  const needle = String(value).trim().toLowerCase();
  if (!needle) return -1;
  let index = haystack.indexOf(needle);
  if (inputType !== 'number') return index;
  while (index >= 0 && (isDigit(haystack[index - 1]) || isDigit(haystack[index + needle.length]))) {
    index = haystack.indexOf(needle, index + 1);
  }
  return index;
};

// Long numbers are where a stray zero slips through, so echo them back grouped and in compact form (2000萬 / 20M).
// react-intl 2.x drops `notation`, and CLDR's zh-Hant-HK compact form is "20M", so HK reads in 萬 via zh-Hant.
const NumberReadout = ({ value, intl }) => {
  const number = Number(value);
  if (isBlank(value) || !Number.isFinite(number) || Math.abs(number) < 10000) return null;
  const locale = (intl.locale || 'en').replace(/-HK$/, '');
  const compact = new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 2 }).format(number);
  return (
    <span className="meeting-factsheet-sync-dialog__readout">
      {intl.formatNumber(number)} ({compact})
    </span>
  );
};

const SuggestionInput = ({ item, value, onChange, intl }) => {
  const label = intl.formatMessage({ id: 'meeting-factsheet-sync-suggested-value' });

  if (item.metadata.inputType === 'enum') {
    return (
      <Box className="meeting-factsheet-sync-dialog__choice-group" role="group" aria-label={label}>
        {item.metadata.options.map(option => {
          const isSelected = value === option;
          return (
            <Button
              key={option}
              className="meeting-factsheet-sync-dialog__choice"
              color="primary"
              size="small"
              variant={isSelected ? 'contained' : 'outlined'}
              aria-pressed={isSelected}
              onClick={() => onChange(option)}
            >
              {intl.formatMessage({ id: option, defaultMessage: option })}
            </Button>
          );
        })}
      </Box>
    );
  }

  return (
    <Fragment>
      <TextField
        className="meeting-factsheet-sync-dialog__input"
        variant="outlined"
        size="small"
        type={item.metadata.inputType}
        value={value ?? ''}
        onChange={event => onChange(event.target.value)}
        slotProps={{
          htmlInput: {
            'aria-label': label,
            ...(item.metadata.inputType === 'number' ? { min: 0, inputMode: 'decimal' } : {})
          }
        }}
        fullWidth
      />
      {item.metadata.inputType === 'number' && <NumberReadout value={value} intl={intl} />}
    </Fragment>
  );
};

const Evidence = ({ item, value, intl }) => {
  const evidence = item.evidence || '';
  const needle = isBlank(value) ? '' : String(value).trim();
  const index = findValueInEvidence(evidence, needle, item.metadata.inputType);

  return (
    <Box className="meeting-factsheet-sync-dialog__evidence">
      <Typography component="span" className="meeting-factsheet-sync-dialog__evidence-label">
        {intl.formatMessage({ id: 'source' })}
      </Typography>
      <Typography component="q" className="meeting-factsheet-sync-dialog__evidence-text">
        {index < 0 ? (
          evidence
        ) : (
          <Fragment>
            {evidence.slice(0, index)}
            <mark>{evidence.slice(index, index + needle.length)}</mark>
            {evidence.slice(index + needle.length)}
          </Fragment>
        )}
      </Typography>
    </Box>
  );
};

const CurrentValue = ({ item, intl }) => {
  const text = displayValue(item.currentValue, item.metadata, intl);
  return (
    <Box className="meeting-factsheet-sync-dialog__current">
      <Typography component="span" className="meeting-factsheet-sync-dialog__value-label">
        {intl.formatMessage({ id: 'current' })}
      </Typography>
      <Typography
        component="span"
        className={`meeting-factsheet-sync-dialog__value ${isBlank(item.currentValue) ? 'is-empty' : ''}`}
        title={text}
      >
        {text}
      </Typography>
    </Box>
  );
};

const isInteractiveTarget = target =>
  typeof HTMLElement !== 'undefined' &&
  target instanceof HTMLElement &&
  target.closest('button, input, textarea, select, label, [role="button"], .meeting-factsheet-sync-dialog__input');

const SuggestionItem = ({ item, selected, value, onToggle, onValueChange, intl }) => {
  const label = displayLabel(item, intl);
  const needsReview = item.confidence !== 'explicit';
  const itemClassName = ['meeting-factsheet-sync-dialog__item', selected ? 'is-selected' : '']
    .filter(Boolean)
    .join(' ');

  // Pointer convenience only; the checkbox stays the single keyboard/AT control for the row.
  const toggleFromContainer = event => {
    if (isInteractiveTarget(event.target)) return;
    onToggle(!selected);
  };

  return (
    <Box className={itemClassName} component="li" aria-label={label} onClick={toggleFromContainer}>
      <Checkbox
        className="meeting-factsheet-sync-dialog__check"
        checked={!!selected}
        onChange={event => onToggle(event.target.checked)}
        slotProps={{ input: { 'aria-label': label } }}
      />

      <Box className="meeting-factsheet-sync-dialog__item-main">
        <Box className="meeting-factsheet-sync-dialog__item-header">
          <Typography component="span" className="meeting-factsheet-sync-dialog__field-label">
            {label}
          </Typography>
          {needsReview && (
            <span className={`meeting-factsheet-sync-dialog__confidence is-${item.confidence}`}>
              {displayConfidence(item.confidence, intl)}
            </span>
          )}
        </Box>

        <Box className="meeting-factsheet-sync-dialog__change">
          <CurrentValue item={item} intl={intl} />
          <ArrowForwardRoundedIcon className="meeting-factsheet-sync-dialog__arrow" aria-hidden="true" />
          <Box className="meeting-factsheet-sync-dialog__suggested">
            <SuggestionInput item={item} value={value} onChange={onValueChange} intl={intl} />
          </Box>
        </Box>

        {item.evidence && <Evidence item={item} value={value} intl={intl} />}
      </Box>
    </Box>
  );
};

const SuggestionGroup = ({ id, title, hint, items, renderItem }) => (
  <Box component="section" className="meeting-factsheet-sync-dialog__group" aria-labelledby={title ? id : undefined}>
    {title && (
      <header className="meeting-factsheet-sync-dialog__group-header">
        <Typography component="h3" id={id} className="meeting-factsheet-sync-dialog__group-title">
          {title}
        </Typography>
        {hint && <Typography className="meeting-factsheet-sync-dialog__group-hint">{hint}</Typography>}
      </header>
    )}
    <ul className="meeting-factsheet-sync-dialog__list">{items.map(renderItem)}</ul>
  </Box>
);

export const MeetingFactsheetSyncDialog = props => {
  const { intl, meetingFactsheetSync, toggleDialog, getClientById } = props;
  const [selected, setSelected] = useState({});
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const suggestions = meetingFactsheetSync ? meetingFactsheetSync.suggestions || EMPTY_SUGGESTIONS : EMPTY_SUGGESTIONS;
  const isAnalysisError = !!meetingFactsheetSync?.analysisError;
  const isOpen = !!meetingFactsheetSync && !meetingFactsheetSync.closed;

  useEffect(() => {
    const nextSelected = {};
    const nextValues = {};
    suggestions.forEach(item => {
      nextSelected[item.field] = item.confidence === 'explicit';
      nextValues[item.field] = item.value;
    });
    setSelected(nextSelected);
    setValues(nextValues);
    setSaving(false);
  }, [meetingFactsheetSync, suggestions]);

  const selectedSuggestions = useMemo(
    () => suggestions.filter(item => selected[item.field] && values[item.field] !== '' && values[item.field] !== null),
    [selected, suggestions, values]
  );
  const selectedCount = useMemo(() => suggestions.filter(item => selected[item.field]).length, [selected, suggestions]);
  const explicitItems = useMemo(() => suggestions.filter(item => item.confidence === 'explicit'), [suggestions]);
  const reviewItems = useMemo(() => suggestions.filter(item => item.confidence !== 'explicit'), [suggestions]);

  const setAll = checked =>
    setSelected(suggestions.reduce((result, item) => ({ ...result, [item.field]: checked }), {}));

  if (!meetingFactsheetSync) return null;

  const requestClose = () =>
    toggleDialog('meetingFactsheetSync', {
      ...meetingFactsheetSync,
      closed: true
    });

  const close = () => {
    if (!saving) requestClose();
  };

  const onExited = () => toggleDialog('meetingFactsheetSync', false);

  const confirm = async () => {
    if (!selectedSuggestions.length) {
      close();
      return;
    }

    const payload = buildMeetingFactsheetSyncPayload(suggestions, selected, values);
    if (!payload) {
      toast.error(IconToast('error', intl.formatMessage({ id: 'meeting-factsheet-sync-invalid-data' })), {
        className: 'new-toast'
      });
      return;
    }

    setSaving(true);

    try {
      const response = await updateFactsheetById({
        mode: 'meeting-note-sync',
        factsheetId: meetingFactsheetSync.factsheetId,
        ...payload
      });

      if (!response.success) {
        if (response.conflict) {
          toast.info(IconToast('info', intl.formatMessage({ id: 'meeting-factsheet-sync-conflict' })), {
            className: 'new-toast'
          });
          getClientById(meetingFactsheetSync.clientId);
          requestClose();
          return;
        }
        throw new Error(response.message || 'Factsheet sync failed');
      }

      trackDirect('meetingFactsheetSyncAdopted', { count: selectedSuggestions.length });
      if (meetingFactsheetSync.onApplied) meetingFactsheetSync.onApplied(response.data);
      getClientById(meetingFactsheetSync.clientId);
      toast.info(
        IconToast(
          'success',
          intl.formatMessage({ id: 'meeting-factsheet-sync-updated' }, { count: selectedSuggestions.length })
        ),
        {
          className: 'new-toast'
        }
      );
      requestClose();
    } catch (error) {
      if (isMeetingFactsheetSyncConflict(error)) {
        toast.info(IconToast('info', intl.formatMessage({ id: 'meeting-factsheet-sync-conflict' })), {
          className: 'new-toast'
        });
        getClientById(meetingFactsheetSync.clientId);
        requestClose();
        return;
      }
      setSaving(false);
      toast.error(IconToast('error', intl.formatMessage({ id: 'meeting-factsheet-sync-update-error' })), {
        className: 'new-toast'
      });
    }
  };

  const retry = () => {
    if (meetingFactsheetSync.onRetry) meetingFactsheetSync.onRetry();
  };

  const renderItem = item => (
    <SuggestionItem
      key={item.field}
      item={item}
      selected={selected[item.field]}
      value={values[item.field]}
      onToggle={checked => setSelected(previous => ({ ...previous, [item.field]: checked }))}
      onValueChange={nextValue => {
        setValues(previous => ({ ...previous, [item.field]: nextValue }));
        setSelected(previous => ({ ...previous, [item.field]: true }));
      }}
      intl={intl}
    />
  );

  const total = suggestions.length;

  return (
    <Dialog
      className="meeting-factsheet-sync-dialog"
      open={isOpen}
      onClose={close}
      fullWidth
      maxWidth="sm"
      // Sits above the meeting-note bottom sheet (react-spring-bottom-sheet overlay is z-index 1300).
      sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
      slots={{ transition: SlideTransition }}
      slotProps={{ transition: { onExited } }}
      aria-labelledby="meeting-factsheet-sync-dialog-title"
      aria-describedby={isAnalysisError ? undefined : 'meeting-factsheet-sync-dialog-subtitle'}
    >
      <DialogTitle id="meeting-factsheet-sync-dialog-header" className="meeting-factsheet-sync-dialog__header">
        <span className="meeting-factsheet-sync-dialog__title-row">
          <span id="meeting-factsheet-sync-dialog-title" className="meeting-factsheet-sync-dialog__title">
            {intl.formatMessage({
              id: isAnalysisError ? 'meeting-factsheet-sync-reanalyze-title' : 'meeting-factsheet-sync-title'
            })}
          </span>
          {!isAnalysisError && (
            <span className="meeting-factsheet-sync-dialog__ai-badge">
              <WandSparkles size={12} aria-hidden="true" />
              {intl.formatMessage({ id: 'meeting-factsheet-sync-ai-suggestions' })}
            </span>
          )}
        </span>
        {!isAnalysisError && (
          <span id="meeting-factsheet-sync-dialog-subtitle" className="meeting-factsheet-sync-dialog__subtitle">
            {intl.formatMessage({ id: 'meeting-factsheet-sync-instruction' })}
          </span>
        )}
      </DialogTitle>
      <IconButton
        className="meeting-factsheet-sync-dialog__close"
        onClick={close}
        disabled={saving}
        aria-label={intl.formatMessage({ id: 'Close' })}
      >
        <ClearRoundedIcon />
      </IconButton>

      {/* The meeting-note sheet holds an iOS body-scroll-lock that cancels touchmove outside this marker. */}
      <DialogContent className="meeting-factsheet-sync-dialog__body" data-body-scroll-lock-ignore="true">
        {isAnalysisError ? (
          <Alert severity="warning">{intl.formatMessage({ id: 'meeting-factsheet-sync-analysis-error' })}</Alert>
        ) : (
          <Fragment>
            <Box className="meeting-factsheet-sync-dialog__toolbar">
              <label className="meeting-factsheet-sync-dialog__select-all">
                <Checkbox
                  className="meeting-factsheet-sync-dialog__check"
                  checked={total > 0 && selectedCount === total}
                  indeterminate={selectedCount > 0 && selectedCount < total}
                  onChange={event => setAll(event.target.checked)}
                />
                {intl.formatMessage({ id: 'select-all' })}
              </label>
              <span className="meeting-factsheet-sync-dialog__extracted-count">
                {intl.formatMessage({ id: 'meeting-factsheet-sync-extracted-count' }, { count: total })}
              </span>
            </Box>

            {explicitItems.length > 0 && (
              <SuggestionGroup
                id="meeting-factsheet-sync-group-explicit"
                title={reviewItems.length > 0 && intl.formatMessage({ id: 'meeting-factsheet-sync-group-explicit' })}
                items={explicitItems}
                renderItem={renderItem}
              />
            )}
            {reviewItems.length > 0 && (
              <SuggestionGroup
                id="meeting-factsheet-sync-group-review"
                title={intl.formatMessage({ id: 'meeting-factsheet-sync-group-review' })}
                hint={intl.formatMessage({ id: 'meeting-factsheet-sync-group-review-hint' })}
                items={reviewItems}
                renderItem={renderItem}
              />
            )}
          </Fragment>
        )}
      </DialogContent>

      <DialogActions className="meeting-factsheet-sync-dialog__footer">
        {isAnalysisError ? (
          <Fragment>
            <Button onClick={close} disabled={saving}>
              {intl.formatMessage({ id: 'Close' })}
            </Button>
            <Button onClick={retry} variant="contained" disabled={saving}>
              {intl.formatMessage({ id: 'Retry' })}
            </Button>
          </Fragment>
        ) : (
          <Fragment>
            <Button onClick={close} disabled={saving}>
              {intl.formatMessage({ id: 'Cancel' })}
            </Button>
            <Button onClick={confirm} variant="contained" disabled={saving || (total > 0 && !selectedCount)}>
              {saving
                ? intl.formatMessage({ id: 'meeting-factsheet-sync-updating' })
                : intl.formatMessage({ id: 'meeting-factsheet-sync-confirm-count' }, { selected: selectedCount })}
            </Button>
          </Fragment>
        )}
      </DialogActions>
    </Dialog>
  );
};

const container = connect(state => ({ meetingFactsheetSync: state.control.meetingFactsheetSyncDialog }), {
  toggleDialog,
  getClientById
})(MeetingFactsheetSyncDialog);

export default injectIntl(container);
